import axios from 'axios'
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { toast } from 'react-toastify'

import { getDefaultHeaders } from '../../utils/getDefaultHeaders'
import Button from '../Button'
import ConfirmationDialog from '../ConfirmationDialog'
import ContentTwoFa from './ContentTwoFa'
import { TFaData, TRecoveryCode, TSubmitQRCode } from './types'

type TTwoFactorAuth = {
  baseFaUrl?: string
  buttonElement?: React.ReactElement
  strictMode?: boolean
  externalOpen?: {
    opened: boolean
    setOpened: Dispatch<SetStateAction<boolean>>
  }
}

const TwoFactorAuth = (props: TTwoFactorAuth): React.ReactElement => {
  const {
    baseFaUrl = '',
    buttonElement,
    strictMode = false,
    externalOpen,
  } = props
  const [openModalIntern, setOpenModalIntern] = useState(false)
  const [FaData, setFaData] = useState<TFaData>({})
  const [current, setCurrent] = useState(0)
  const [faMethod, setFaMethod] = useState<string | null | undefined>(null)
  const [loadingValidation, setLoadingValidation] = useState(false)
  const [twoFaEnabled, setTwoFaEnabled] = useState(false)
  const [recoveryCodes, setRecoveryCodes] = useState<TRecoveryCode[]>([])
  const [initialRequestDone, setInitialRequestDone] = useState(false)
  const [successValidation, setSuccessValidation] = useState<boolean | null>(
    null,
  )
  const [recentlyEnabledTwoFaOrRefreshed, setRecentlyEnabledTwoFaOrRefreshed] =
    useState(false)

  const openModal = externalOpen ? externalOpen.opened : openModalIntern
  const setOpenModal = externalOpen
    ? externalOpen.setOpened
    : setOpenModalIntern

  useEffect(() => {
    if (strictMode && !twoFaEnabled && initialRequestDone) {
      setOpenModal(true)
    }
  }, [twoFaEnabled, strictMode, initialRequestDone])

  useEffect(() => {
    getStatusFA()
    setFaData({})
  }, [])

  useEffect(() => {
    if (!openModal) {
      setRecentlyEnabledTwoFaOrRefreshed(false)
    }
  }, [openModal])

  const getStatusFA = (): void => {
    axios
      .get(`${baseFaUrl}/api/2fa/status`, { ...getDefaultHeaders() })
      .then(({ data }) => {
        const { enabled } = data
        setTwoFaEnabled(enabled)
        setInitialRequestDone(true)
        if (!enabled) {
          axios
            .get(`${baseFaUrl}/api/2fa/prepare`, { ...getDefaultHeaders() })
            .then(({ data }) => {
              setFaData(data)
            })
            .catch((error) => {
              const { message = 'Error' } = error
              toast.error(message)
            })
        } else {
          setSuccessValidation(true)
          //setTimeout(() => setCurrent(2), 1000)
        }
      })
      .catch((error) => {
        const { message = 'Error' } = error
        toast.error(message)
      })
    return
  }
  const submitQRCode: TSubmitQRCode = ({
    success = false,
    recovery_codes = [],
  }) => {
    if (success) {
      setSuccessValidation(true)
      setLoadingValidation(false)
      setTwoFaEnabled(true)
      setRecentlyEnabledTwoFaOrRefreshed(true)
      setRecoveryCodes(recovery_codes)
      setTimeout(() => setCurrent(2), 200)
    } else {
      setSuccessValidation(false)
      setLoadingValidation(false)
    }
  }

  const renewRecoveryCodes = (): void => {
    axios
      .post(
        `${baseFaUrl}/api/2fa/recovery`,
        {},
        {
          ...getDefaultHeaders(),
        },
      )
      .then(({ data }) => {
        setRecoveryCodes(data)
        setRecentlyEnabledTwoFaOrRefreshed(true)
      })
      .catch((error) => {
        const { message = 'Error' } = error
        toast.error(message)
      })
  }

  const reset2Fa = (): void => {
    if (strictMode) {
      window.location.reload()
    } else {
      setSuccessValidation(null)
      setFaMethod(undefined)
      setFaData({})
      setTwoFaEnabled(false)
      setCurrent(0)
      setRecentlyEnabledTwoFaOrRefreshed(false)
    }
  }

  const deactiveFa = (): void => {
    axios
      .post(
        `${baseFaUrl}/api/2fa/deactivate`,
        {},
        {
          ...getDefaultHeaders(),
        },
      )
      .then(({ data }) => {
        reset2Fa()
        getStatusFA()
      })
      .catch((error) => {
        const { message = 'Error' } = error
        toast.error(message)
      })
  }

  return (
    <>
      {buttonElement &&
        React.cloneElement(
          buttonElement,
          externalOpen
            ? {}
            : {
                onClick: () => {
                  setOpenModal((c) => !c)
                },
              },
        )}
      <Modal
        show={openModal}
        onHide={() => {
          setOpenModal(false)
        }}
        backdrop={strictMode && !twoFaEnabled ? 'static' : true}
      >
        <Modal.Header
          closeButton={strictMode && !twoFaEnabled ? false : true}
          placeholder=""
        >
          <h4>{i18n.t(`fa2_header`, `Zwei-Faktor-Authentifizierung`)}</h4>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <ContentTwoFa
            FaData={FaData}
            faMethod={faMethod}
            setFaMethod={setFaMethod}
            loadingValidation={loadingValidation}
            recoveryCodes={recoveryCodes}
            twoFaEnabled={twoFaEnabled}
            successValidation={successValidation}
            current={current}
            setCurrent={setCurrent}
            submitQRCode={submitQRCode}
            setLoadingValidation={setLoadingValidation}
            baseFaUrl={baseFaUrl}
            recentlyEnabledTwoFaOrRefreshed={recentlyEnabledTwoFaOrRefreshed}
          />
        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-between">
            {twoFaEnabled && (
              <ConfirmationDialog
                warning
                confirmText={i18n.t(`2fa_deactive-confirm`, `2FA Deaktivieren`)}
                cancelText={i18n.t(`2fa_deactive-cancel`, `Abbrechen`)}
                onConfirm={deactiveFa}
                noHeader
                size="sm"
                text={
                  <div className="d-flex flex-column align-items-center justify-content-center">
                    <h4>
                      {i18n.t(
                        `2fa_question-deactive`,
                        `Zwei-Faktor-Authentifizierung wirklich deaktivieren?`,
                      )}
                    </h4>
                    <span className="text-warning">
                      {i18n.t(
                        `2fa_question-deactive-desc`,
                        `Sie können 2FA später neu einrichten`,
                      )}
                    </span>
                  </div>
                }
              >
                <Button danger className="mr-2">
                  {i18n.t(`2fa_deactive`, `Deaktivieren`)}
                </Button>
              </ConfirmationDialog>
            )}

            {twoFaEnabled && (
              <Button primary onClick={renewRecoveryCodes}>
                {i18n.t(
                  `2fa_renew_codes`,
                  `Neue Wiederherstellungscodes anfordern`,
                )}
              </Button>
            )}

            {strictMode && twoFaEnabled && recentlyEnabledTwoFaOrRefreshed && (
              <Button
                success
                onClick={() => window.location.reload()}
                className="ml-2"
              >
                {i18n.t(`strict_mode_confirm`, `Setup beenden`)}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default TwoFactorAuth
